// Helper functions extracted from flowRelations.js
import { getAncestorsFlowItemIds } from "./flowRelations";
import tryParseJSON from "../helpers/tryParseJSON";

// --- Helper: Checks if a value is a non-empty array ---
export function isNonEmptyArray(arr) {
    return Array.isArray(arr) && arr.length > 0;
}

// --- Helper: Checks if parent/child are valid and not equal/null ---
export function isValidParentChild(parentId, childId) {
    return parentId !== childId && parentId != null && childId != null;
}

// Helper function: Check cache validity
export function updateCacheIfStateChanged(state, loopLastStateSeenRef, loopCachedAnswersRef) {
    if (state !== loopLastStateSeenRef.value) {
        loopLastStateSeenRef.value = state;
        loopCachedAnswersRef.value = {};
    }
}

// --- Helper: Recursively checks for a loop in children ---
export function hasLoopInChildren(state, parentChildren, childId, isLoopDetected) {
    return parentChildren.some(child => child === childId || isLoopDetected(state, child, childId));
}

/**
 * Recursively detects if adding a child would create a loop in the flow.
 * Returns true if a loop is detected, otherwise false.
 */
export function detectLoopRecursively(state, parentChildren, childId, isLoopDetected) {
    if (!isNonEmptyArray(parentChildren)) return false;
    // Use extracted helpers for clarity and reduced complexity
    return hasLoopInChildren(state, parentChildren, childId, isLoopDetected);
}

// --- Lookup Set for relevant flow item types ---
const RELEVANT_FLOW_ITEM_TYPES = new Set([
    "export",
    "multiexport",
    "singleview",
    "discovery",
    "discoveryinsights",
    "insights",
    "model",
]);

/**
 * Returns an array of relevant flow item types for ancestor field collection.
 */
export function getRelevantFlowItemTypes() {
    // Return as array for compatibility
    return Array.from(RELEVANT_FLOW_ITEM_TYPES);
}

// --- Common Filtering Helpers ---
/**
 * Returns true if the value is a non-empty string.
 */
export function isNonEmptyString(val) {
    return typeof val === "string" && val.trim().length > 0;
}

/**
 * Returns true if the value is not null, undefined, or an empty string.
 */
export function isPresent(val) {
    return val !== null && val !== undefined && val !== "";
}

/**
 * Filters an array of objects by a property being a non-empty string.
 * @param {Array} arr - The array to filter.
 * @param {string} prop - The property name to check.
 */
export function filterByNonEmptyString(arr, prop) {
    return Array.isArray(arr) ? arr.filter(x => isNonEmptyString(x[prop])) : [];
}

/**
 * Filters an array of objects by a property being non-null and non-empty.
 * @param {Array} arr - The array to filter.
 * @param {string} prop - The property name to check.
 */
export function filterByNonNullOrEmpty(arr, prop) {
    return Array.isArray(arr) ? arr.filter(x => isPresent(x[prop])) : [];
}

/**
 * Filters an array of objects by IsActive === true.
 */
export function filterByActiveStatus(arr) {
    return Array.isArray(arr) ? arr.filter(x => x.IsActive === true) : [];
}

/**
 * Collects relevant flow item IDs for a given flow and types.
 * Returns an array of FlowItemIds.
 */
export function getRelevantFlowItemIds(flowItems, flowId, relevantTypes = RELEVANT_FLOW_ITEM_TYPES) {
    if (!Array.isArray(flowItems) || !relevantTypes) return [];
    const isRelevant =
        relevantTypes instanceof Set ? type => relevantTypes.has(type) : type => relevantTypes.includes(type);
    return filterByActiveStatus(
        flowItems.filter(flowItem => flowItem.FlowId === flowId && isRelevant(flowItem.FlowItemType))
    ).map(x => x.FlowItemId);
}

/**
 * Collects ancestor filter/case objects for given flow item IDs.
 * Returns an object with ancestorsFilter and ancestorsCase arrays.
 */
export function getAncestorsObjects(flowRelations, flowItemIds, filterArray, caseArray) {
    if (!Array.isArray(flowItemIds)) return { ancestorsFilter: [], ancestorsCase: [] };
    let ancestorsFilter = [];
    let ancestorsCase = [];
    for (const flowItemId of flowItemIds) {
        const ancestorIds = [];
        getAncestorsFlowItemIds(flowRelations, flowItemId, ancestorIds);
        ancestorsFilter = ancestorsFilter.concat(
            filterArray.filter(x => ancestorIds.includes(x.FlowItemId) && isNonEmptyString(x.FlowFilterCriteria))
        );
        ancestorsCase = ancestorsCase.concat(
            caseArray.filter(x => ancestorIds.includes(x.FlowItemId) && isNonEmptyString(x.FlowCaseCriteria))
        );
    }
    return { ancestorsFilter, ancestorsCase };
}

/**
 * Processes and pushes used fields from ancestor objects into usedFields array.
 * Now avoids processing the same ancestor multiple times.
 */
export function processAncestorsAndPushFields(
    ancestorsFilter,
    ancestorsCase,
    flowItems,
    filters,
    usedFields,
    fieldsById
) {
    if (!Array.isArray(ancestorsFilter) || !Array.isArray(ancestorsCase)) return;

    const processedAncestorIds = new Set(); // Set to avoid duplication
    const flowItemsMap = Object.fromEntries(flowItems.map(item => [item.FlowItemId, item]));
    ancestorsFilter.forEach(filter => {
        if (processedAncestorIds.has(filter.FlowItemId)) return;
        processedAncestorIds.add(filter.FlowItemId); // Avoid Filter duplication

        const includeObject = tryParseJSON(filter.FlowFilterCriteria) || {};
        const flowItem = flowItemsMap[filter.FlowItemId];
        if (flowItem) {
            pushUsedFields(includeObject, filters, flowItem, usedFields, fieldsById);
        }
    });

    ancestorsCase.forEach(flowItemCase => {
        if (processedAncestorIds.has(flowItemCase.FlowItemId)) return;
        processedAncestorIds.add(flowItemCase.FlowItemId); // Avoid case duplication

        const includeObjectCase = tryParseJSON(flowItemCase.FlowCaseCriteria) || {};
        const flowItem = flowItemsMap[flowItemCase.FlowItemId];
        if (flowItem) {
            pushUsedFields(includeObjectCase, filters, flowItem, usedFields, fieldsById);
        }
    });
}

/**
 * Collects ancestor fields for given flow items.
 * Returns an array of used fields.
 */
export function getAncestorFieldsForFlowItems(
    flowRelations,
    filters,
    flowItems,
    filterArray,
    caseArray,
    flowId,
    fieldsById
) {
    const usedFields = [];
    const relevantFlowItemTypes = getRelevantFlowItemTypes();
    const flowItemIds = getRelevantFlowItemIds(flowItems, flowId, relevantFlowItemTypes);
    if (fieldsById) {
        const { ancestorsFilter, ancestorsCase } = getAncestorsObjects(
            flowRelations,
            flowItemIds,
            filterArray,
            caseArray
        );
        processAncestorsAndPushFields(ancestorsFilter, ancestorsCase, flowItems, filters, usedFields, fieldsById);
    }
    return usedFields;
}

// --- Helper: Safely get rules array from includeObject ---
function getRulesArray(includeObject) {
    return includeObject && Array.isArray(includeObject.rules) ? includeObject.rules : [];
}

/**
 * Pushes used fields from includeObject into usedFields array, recursing for nested rules.
 */
export function pushUsedFields(includeObject, filters, flowItem, usedFields, fieldsById) {
    const rules = getRulesArray(includeObject);
    for (const field of rules) {
        if (!field) continue;
        // If the rule is a group, recurse
        if (field.rules && Array.isArray(field.rules)) {
            pushUsedFields(field, filters, flowItem, usedFields, fieldsById);
        } else if (field.id != null) {
            // Only push if the field exists in fieldsById and not already in usedFields

            if (fieldsById && fieldsById[field.id] && !usedFields.some(usedField => usedField.FieldId === field.id)) {
                usedFields.push({
                    FlowItemId: flowItem ? flowItem.FlowItemId : 0,
                    FlowItemType: flowItem ? flowItem.FlowItemType : "Unknown",
                    FlowItemName: flowItem ? flowItem.FlowItemName : "Unknown",
                    FieldId: field.id,
                    FieldName: field.field,
                    FieldTableKey: fieldsById[field.id]?.fieldTableKey,
                    IsThirdParty: fieldsById[field.id]?.IsThirdParty,
                    FieldClassification: fieldsById[field.id]?.FieldClassification,
                });
            }
        }
    }
}

// --- Helper: Cache key for loop detection ---
export function getLoopCacheKey(parentId, childId) {
    return `${parentId}__${childId}`;
}

// --- Helper: Direct child check ---
export function isDirectChild(parentChildren, childId) {
    return Array.isArray(parentChildren) && parentChildren.includes(childId);
}

// --- Helper: Recursive loop check ---
export function recursiveLoopCheck(state, parentChildren, childId, isLoopDetected) {
    return hasLoopInChildren(state, parentChildren, childId, isLoopDetected);
}
